import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Upload,
  RefreshCw,
  ExternalLink,
  Image,
  Loader2,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Package,
  Printer,
  Activity,
  FileText,
  History,
  RotateCcw,
  Copy,
} from 'lucide-react';
import {
  api,
  type Listing,
  type Asset,
  type EtsyCategory,
  type PrintifyConfig,
  type ProcessingJob,
  type ProcessingJobStatus,
  type ContentHistoryItem,
  type ImageHistoryItem,
} from '../api/client';

type ListingWithAssets = Listing & {
  niche_keyword?: string;
  text_model_id?: string;
  image_model_id?: string;
  assets?: Asset[];
};

function buildCategoryPath(categories: EtsyCategory[], categoryId: number): string {
  const categoryMap = new Map<number, EtsyCategory>();
  const buildMap = (cats: EtsyCategory[]) => {
    for (const cat of cats) {
      categoryMap.set(cat.id, cat);
      if (cat.children) buildMap(cat.children);
    }
  };
  buildMap(categories);

  const path: string[] = [];
  let current = categoryMap.get(categoryId);
  while (current) {
    path.unshift(current.name);
    current = current.parent_id ? categoryMap.get(current.parent_id) : undefined;
  }
  return path.join(' > ');
}

function flattenCategories(
  categories: EtsyCategory[],
  level = 0
): Array<EtsyCategory & { indent: number }> {
  const result: Array<EtsyCategory & { indent: number }> = [];
  for (const cat of categories) {
    result.push({ ...cat, indent: level });
    if (cat.children && cat.children.length > 0) {
      result.push(...flattenCategories(cat.children, level + 1));
    }
  }
  return result;
}

function getJobStatusInfo(status: ProcessingJobStatus) {
  switch (status) {
    case 'queued':
      return { icon: Clock, color: 'text-gray-500', bg: 'bg-gray-100', label: 'Queued' };
    case 'processing':
      return {
        icon: Loader2,
        color: 'text-blue-500',
        bg: 'bg-blue-100',
        label: 'Processing',
        animate: true,
      };
    case 'upscaling':
      return {
        icon: Loader2,
        color: 'text-purple-500',
        bg: 'bg-purple-100',
        label: 'Upscaling',
        animate: true,
      };
    case 'mockup_gen':
      return {
        icon: Loader2,
        color: 'text-orange-500',
        bg: 'bg-orange-100',
        label: 'Generating Mockups',
        animate: true,
      };
    case 'complete':
      return { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-100', label: 'Complete' };
    case 'failed':
      return { icon: XCircle, color: 'text-red-500', bg: 'bg-red-100', label: 'Failed' };
    default:
      return { icon: AlertTriangle, color: 'text-gray-500', bg: 'bg-gray-100', label: status };
  }
}

function getJobTypeLabel(jobType: string) {
  switch (jobType) {
    case 'edge_press':
      return 'Edge Press';
    case 'mockup':
      return 'Mockup Generation';
    case 'upscale':
      return 'Image Upscaling';
    case 'image_gen':
      return 'Image Generation';
    default:
      return jobType;
  }
}

/**
 * Parse tags from various formats (JSON array, comma-separated string)
 */
function parseTags(tags: string | string[] | null | undefined): string[] {
  if (!tags) return [];

  let result: string[] = [];
  if (typeof tags === 'string') {
    try {
      // Try parsing as JSON array first
      const parsed = JSON.parse(tags);
      result = Array.isArray(parsed) ? parsed : [tags];
    } catch {
      // Fallback to comma-separated
      result = tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
    }
  } else if (Array.isArray(tags)) {
    result = tags;
  }

  // Clean up any remaining quotes or brackets
  return result.map((t) => t.replace(/^["'[\]]+|["'[\]]+$/g, '').trim()).filter(Boolean);
}

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [categorySearch, setCategorySearch] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<PrintifyConfig | null>(null);
  const [showPrintifyPanel, setShowPrintifyPanel] = useState(false);
  const [showRegeneratePanel, setShowRegeneratePanel] = useState(false);
  const [regenerateConcept, setRegenerateConcept] = useState('');
  const [regenerateProductType, setRegenerateProductType] = useState<'wall_art' | 'apparel'>(
    'wall_art'
  );
  const [showRegenerateContentPanel, setShowRegenerateContentPanel] = useState(false);
  const [selectedRecipeId, setSelectedRecipeId] = useState<string>('');
  const [regenerateContentConcept, setRegenerateContentConcept] = useState('');
  const [showContentHistory, setShowContentHistory] = useState(false);
  const [showImageHistory, setShowImageHistory] = useState(false);
  const [showClonePanel, setShowClonePanel] = useState(false);
  const [cloneConfig, setCloneConfig] = useState<PrintifyConfig | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['listing', id],
    queryFn: () => api.listings.get(id!),
    enabled: !!id,
  });

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['etsy-categories'],
    queryFn: () => api.etsy.categories(),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  const { data: printifyConfigs } = useQuery({
    queryKey: ['printify-configs'],
    queryFn: () => api.printify.configs(),
    staleTime: 1000 * 60 * 5,
  });

  const { data: processingJobsData } = useQuery({
    queryKey: ['processing-jobs', id],
    queryFn: () => api.processingJobs.list({ listing_id: parseInt(id!) }),
    enabled: !!id,
    refetchInterval: (query) => {
      const jobs = query.state.data?.jobs ?? [];
      const hasActiveJobs = jobs.some(
        (job: ProcessingJob) => !['complete', 'failed'].includes(job.status)
      );
      return hasActiveJobs ? 3000 : false;
    },
  });

  const { data: recipesData } = useQuery({
    queryKey: ['recipes'],
    queryFn: () => api.recipes.list(),
    staleTime: 1000 * 60 * 5,
  });

  const { data: contentHistoryData } = useQuery({
    queryKey: ['content-history', id],
    queryFn: () => api.listings.contentHistory(id!),
    enabled: !!id && showContentHistory,
  });

  const { data: imageHistoryData } = useQuery({
    queryKey: ['image-history', id],
    queryFn: () => api.listings.imageHistory(id!),
    enabled: !!id && showImageHistory,
  });

  const updateCategoryMutation = useMutation({
    mutationFn: (categoryId: number) =>
      api.listings.update(parseInt(id!), { etsy_category_id: categoryId } as Partial<Listing>),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listing', id] });
      setShowCategoryDropdown(false);
      setCategorySearch('');
    },
  });

  const syncToEtsyMutation = useMutation({
    mutationFn: () => api.etsy.sync(parseInt(id!)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listing', id] });
      queryClient.invalidateQueries({ queryKey: ['listings'] });
    },
  });

  const createPrintifyProductMutation = useMutation({
    mutationFn: (config: PrintifyConfig) =>
      api.printify.createFromListing({
        listing_id: parseInt(id!),
        blueprint_id: config.blueprint_id,
        print_provider_id: config.print_provider_id,
        price: config.default_price,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listing', id] });
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      setShowPrintifyPanel(false);
      setSelectedConfig(null);
    },
  });

  const publishPrintifyMutation = useMutation({
    mutationFn: () => api.printify.publishListing(parseInt(id!)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listing', id] });
      queryClient.invalidateQueries({ queryKey: ['listings'] });
    },
  });

  const regenerateDesignMutation = useMutation({
    mutationFn: (params: { concept: string; productType: 'wall_art' | 'apparel' }) =>
      api.pipeline.design({
        listing_id: parseInt(id!),
        concept: params.concept,
        product_type: params.productType,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listing', id] });
      queryClient.invalidateQueries({ queryKey: ['image-history', id] });
      setShowRegeneratePanel(false);
      setRegenerateConcept('');
    },
  });

  const regenerateContentMutation = useMutation({
    mutationFn: (params: { concept: string; recipeId?: string }) =>
      api.pipeline.seo({
        listing_id: parseInt(id!),
        concept: params.concept,
        recipe_id: params.recipeId || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listing', id] });
      queryClient.invalidateQueries({ queryKey: ['content-history', id] });
      setShowRegenerateContentPanel(false);
      setSelectedRecipeId('');
    },
  });

  const restoreContentMutation = useMutation({
    mutationFn: (historyId: number) => api.listings.restoreContent(id!, historyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listing', id] });
      queryClient.invalidateQueries({ queryKey: ['content-history', id] });
    },
  });

  const setCurrentImageMutation = useMutation({
    mutationFn: (assetId: number) => api.listings.setCurrentImage(id!, assetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listing', id] });
      queryClient.invalidateQueries({ queryKey: ['image-history', id] });
    },
  });

  const cloneListingMutation = useMutation({
    mutationFn: (configId?: number) => api.listings.clone(id!, { printify_config_id: configId }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      setShowClonePanel(false);
      setCloneConfig(null);
      navigate(`/listing/${result.listing_id}`);
    },
  });

  const listing: ListingWithAssets | undefined = data?.listing;
  const categories = useMemo(() => categoriesData?.categories ?? [], [categoriesData?.categories]);
  const processingJobs = processingJobsData?.jobs ?? [];

  const getStatusIndicator = (status: string, isPod: boolean) => {
    switch (status) {
      case 'draft':
        return { icon: Clock, color: 'text-gray-500', bg: 'bg-gray-100', label: 'Draft' };
      case 'generating':
        return {
          icon: Loader2,
          color: 'text-blue-500',
          bg: 'bg-blue-100',
          label: 'Generating',
          animate: true,
        };
      case 'ready':
        return {
          icon: CheckCircle,
          color: 'text-green-500',
          bg: 'bg-green-100',
          label: isPod ? 'Ready' : 'Ready to Sync',
        };
      case 'syncing':
        return {
          icon: Loader2,
          color: 'text-orange-500',
          bg: 'bg-orange-100',
          label: isPod ? 'Syncing to Printify' : 'Syncing to Etsy',
          animate: true,
        };
      case 'published':
        return {
          icon: CheckCircle,
          color: 'text-purple-500',
          bg: 'bg-purple-100',
          label: isPod ? 'In Printify' : 'Published on Etsy',
        };
      case 'printify_draft':
        return {
          icon: CheckCircle,
          color: 'text-purple-500',
          bg: 'bg-purple-100',
          label: 'Printify Draft',
        };
      case 'sync_failed':
        return { icon: XCircle, color: 'text-red-500', bg: 'bg-red-100', label: 'Sync Failed' };
      default:
        return { icon: AlertTriangle, color: 'text-gray-500', bg: 'bg-gray-100', label: status };
    }
  };

  // Only show "Sync to Etsy" for digital downloads, not POD products (POD syncs through Printify)
  const isPodProduct = listing?.product_type === 'printify_pod' || listing?.printify_product_id;
  const canSync =
    !isPodProduct && (listing?.status === 'ready' || listing?.status === 'sync_failed');

  const flatCategories = useMemo(() => flattenCategories(categories), [categories]);

  const filteredCategories = useMemo(() => {
    if (!categorySearch.trim()) return flatCategories.slice(0, 100);
    const search = categorySearch.toLowerCase();
    return flatCategories.filter((cat) => cat.name.toLowerCase().includes(search)).slice(0, 100);
  }, [flatCategories, categorySearch]);

  const currentCategoryPath = useMemo(() => {
    if (!listing?.etsy_category_id || categories.length === 0) return null;
    return buildCategoryPath(categories, listing.etsy_category_id);
  }, [listing, categories]);

  // Detect if this listing uses an apparel blueprint
  const detectedApparelProduct = useMemo(() => {
    // Check from printify config
    if (listing?.printify_blueprint_id && printifyConfigs?.configs) {
      const matchingConfig = printifyConfigs.configs.find(
        (c) => c.blueprint_id === listing.printify_blueprint_id
      );
      const apparelTypes = ['tshirt', 't-shirt', 'hoodie', 'sweatshirt', 'apparel', 'clothing'];
      if (
        matchingConfig &&
        apparelTypes.some((t) => matchingConfig.product_type.toLowerCase().includes(t))
      ) {
        return true;
      }
    }
    // Check from title keywords
    if (listing?.title) {
      const apparelKeywords = [
        'shirt',
        'hoodie',
        'sweatshirt',
        'sweater',
        'tee',
        'top',
        'clothing',
        'apparel',
      ];
      const titleLower = listing.title.toLowerCase();
      if (apparelKeywords.some((k) => titleLower.includes(k))) {
        return true;
      }
    }
    return false;
  }, [listing, printifyConfigs]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="text-center py-24">
        <p className="text-gray-500">Listing not found.</p>
        <Link to="/inventory" className="text-etsy-orange hover:underline mt-2 inline-block">
          Back to Inventory
        </Link>
      </div>
    );
  }

  const primaryAsset = listing.assets
    ?.filter((a) => a.asset_type === 'primary')
    .sort((a, b) => b.id - a.id)[0];
  const mockups = listing.assets?.filter((a) => a.asset_type === 'mockup') ?? [];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Link to="/inventory" className="btn btn-secondary p-2">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {listing.title || 'Untitled Listing'}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-sm text-gray-500">ID: {listing.id}</span>
            {listing.niche_keyword && (
              <>
                <span className="text-gray-300">|</span>
                <span className="text-sm text-gray-500">Niche: {listing.niche_keyword}</span>
              </>
            )}
            <span className="text-gray-300">|</span>
            {(() => {
              const statusInfo = getStatusIndicator(listing.status, !!isPodProduct);
              const StatusIcon = statusInfo.icon;
              return (
                <span
                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.color}`}
                >
                  <StatusIcon
                    className={`w-3.5 h-3.5 ${statusInfo.animate ? 'animate-spin' : ''}`}
                  />
                  {statusInfo.label}
                </span>
              );
            })()}
          </div>
        </div>
        <div className="flex gap-2">
          {primaryAsset && (
            <button
              onClick={() => {
                setCloneConfig(null);
                setShowClonePanel(true);
              }}
              className="btn btn-secondary flex items-center gap-2"
            >
              <Copy className="w-4 h-4" />
              Create Another Product
            </button>
          )}
          <button
            onClick={() => {
              setRegenerateConcept('');
              setRegenerateProductType(detectedApparelProduct ? 'apparel' : 'wall_art');
              setShowRegeneratePanel(true);
            }}
            className="btn btn-secondary flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Regenerate Design
          </button>
          <button
            onClick={() => {
              setSelectedRecipeId('');
              setRegenerateContentConcept(listing.concept || listing.title || '');
              setShowRegenerateContentPanel(true);
            }}
            className="btn btn-secondary flex items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Regenerate Content
          </button>
          {canSync && (
            <button
              onClick={() => syncToEtsyMutation.mutate()}
              disabled={syncToEtsyMutation.isPending || listing.status === 'syncing'}
              className="btn btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {syncToEtsyMutation.isPending || listing.status === 'syncing' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  {listing.status === 'sync_failed' ? 'Retry Sync' : 'Sync to Etsy'}
                </>
              )}
            </button>
          )}
          {listing.etsy_listing_id && (
            <a
              href={`https://www.etsy.com/listing/${listing.etsy_listing_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              View on Etsy
            </a>
          )}
        </div>
      </div>

      {/* Sync Error Message */}
      {listing.status === 'sync_failed' && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-800">Sync Failed</h3>
            <p className="text-sm text-red-600 mt-1">
              {isPodProduct
                ? 'There was an error syncing this listing to Printify. Please check your Printify connection and try again.'
                : 'There was an error syncing this listing to Etsy. Please check your Etsy connection and try again.'}
            </p>
          </div>
        </div>
      )}

      {/* Sync Success Message */}
      {syncToEtsyMutation.isSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-green-800">Successfully Published</h3>
            <p className="text-sm text-green-600 mt-1">Your listing has been synced to Etsy.</p>
          </div>
        </div>
      )}

      {/* Sync Error from Mutation */}
      {syncToEtsyMutation.isError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-800">Sync Error</h3>
            <p className="text-sm text-red-600 mt-1">
              {syncToEtsyMutation.error instanceof Error
                ? syncToEtsyMutation.error.message
                : 'Failed to sync listing'}
            </p>
          </div>
        </div>
      )}

      {/* Regenerate Design Panel */}
      {showRegeneratePanel && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Image className="w-5 h-5 text-blue-500" />
              <h3 className="font-medium text-blue-800">Regenerate Design</h3>
            </div>
            <button
              onClick={() => setShowRegeneratePanel(false)}
              className="text-blue-500 hover:text-blue-700"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-3">
            <div>
              <label className="label text-blue-800">Design Concept</label>
              <textarea
                value={regenerateConcept}
                onChange={(e) => setRegenerateConcept(e.target.value)}
                placeholder="Describe the design you want to generate (e.g., 'vintage library card with floral elements')"
                className="input w-full h-24 resize-none"
              />
            </div>
            <div>
              <label className="label text-blue-800">Design Type</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setRegenerateProductType('wall_art')}
                  className={`flex-1 p-3 rounded-lg border transition-colors ${
                    regenerateProductType === 'wall_art'
                      ? 'border-blue-500 bg-blue-100 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium">Wall Art</div>
                  <div className="text-xs text-gray-500 mt-1">Full printable artwork</div>
                </button>
                <button
                  type="button"
                  onClick={() => setRegenerateProductType('apparel')}
                  className={`flex-1 p-3 rounded-lg border transition-colors ${
                    regenerateProductType === 'apparel'
                      ? 'border-purple-500 bg-purple-100 text-purple-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium">Apparel</div>
                  <div className="text-xs text-gray-500 mt-1">Flat graphic for printing</div>
                </button>
              </div>
              {detectedApparelProduct && regenerateProductType === 'apparel' && (
                <p className="text-xs text-purple-600 mt-1 flex items-center gap-1">
                  <Package className="w-3 h-3" />
                  Auto-detected from title keywords
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowRegeneratePanel(false)}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  regenerateDesignMutation.mutate({
                    concept: regenerateConcept,
                    productType: regenerateProductType,
                  })
                }
                disabled={!regenerateConcept.trim() || regenerateDesignMutation.isPending}
                className="btn btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {regenerateDesignMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Generate {regenerateProductType === 'apparel' ? 'Apparel Design' : 'Wall Art'}
                  </>
                )}
              </button>
            </div>
            {regenerateDesignMutation.isError && (
              <p className="text-sm text-red-600 text-center">
                {regenerateDesignMutation.error instanceof Error
                  ? regenerateDesignMutation.error.message
                  : 'Failed to regenerate design'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Regenerate Content Panel */}
      {showRegenerateContentPanel && (
        <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-500" />
              <h3 className="font-medium text-purple-800">Regenerate Content</h3>
            </div>
            <button
              onClick={() => setShowRegenerateContentPanel(false)}
              className="text-purple-500 hover:text-purple-700"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-3">
            <p className="text-sm text-purple-700">
              Regenerate title, tags, and description for this listing. Optionally apply a recipe
              for custom style and tone.
            </p>
            <div>
              <label className="label text-purple-800">Design Concept</label>
              <textarea
                value={regenerateContentConcept}
                onChange={(e) => setRegenerateContentConcept(e.target.value)}
                placeholder="Describe the design concept (e.g., 'Cozy mug design featuring a book and tea')"
                className="input w-full h-20 resize-none"
              />
              <p className="text-xs text-purple-600 mt-1">
                The concept describes what the design is about - used to generate SEO content
              </p>
            </div>
            <div>
              <label className="label text-purple-800">Recipe (Optional)</label>
              <select
                value={selectedRecipeId}
                onChange={(e) => setSelectedRecipeId(e.target.value)}
                className="input w-full"
              >
                <option value="">No recipe - use default prompts</option>
                {recipesData?.recipes.map((recipe) => (
                  <option key={recipe.id} value={recipe.id}>
                    {recipe.name}
                  </option>
                ))}
              </select>
              {selectedRecipeId && (
                <p className="text-xs text-purple-600 mt-1">
                  Recipe will influence the style and tone of generated content
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowRegenerateContentPanel(false)}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  regenerateContentMutation.mutate({
                    concept: regenerateContentConcept,
                    recipeId: selectedRecipeId || undefined,
                  })
                }
                disabled={!regenerateContentConcept.trim() || regenerateContentMutation.isPending}
                className="btn btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {regenerateContentMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Regenerate Content
                  </>
                )}
              </button>
            </div>
            {regenerateContentMutation.isError && (
              <p className="text-sm text-red-600 text-center">
                {regenerateContentMutation.error instanceof Error
                  ? regenerateContentMutation.error.message
                  : 'Failed to regenerate content'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Clone Listing Panel */}
      {showClonePanel && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Copy className="w-5 h-5 text-green-600" />
              <h3 className="font-medium text-green-800">Create Another Product</h3>
            </div>
            <button
              onClick={() => setShowClonePanel(false)}
              className="text-green-500 hover:text-green-700"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-3">
            <p className="text-sm text-green-700">
              Create a new listing using the same design image. Select a product type to create a
              POD product, or leave empty for a digital download.
            </p>
            <div>
              <label className="label text-green-800">Product Type (Optional)</label>
              {printifyConfigs?.configs && printifyConfigs.configs.length > 0 ? (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  <button
                    onClick={() => setCloneConfig(null)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      cloneConfig === null
                        ? 'border-green-500 bg-green-100'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">Digital Download</div>
                    <p className="text-sm text-gray-500 mt-1">
                      Create as a digital download (no POD)
                    </p>
                  </button>
                  {printifyConfigs.configs.map((config) => (
                    <button
                      key={config.id}
                      onClick={() => setCloneConfig(config)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        cloneConfig?.id === config.id
                          ? 'border-green-500 bg-green-100'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{config.name}</span>
                        {config.is_default === 1 && (
                          <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        ${(config.default_price / 100).toFixed(2)} · {config.product_type}
                      </p>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
                  No product presets configured. The listing will be created as a digital download.
                  Go to Settings to add POD product presets.
                </p>
              )}
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowClonePanel(false)} className="btn btn-secondary flex-1">
                Cancel
              </button>
              <button
                onClick={() => cloneListingMutation.mutate(cloneConfig?.id)}
                disabled={cloneListingMutation.isPending}
                className="btn btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {cloneListingMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Create {cloneConfig ? cloneConfig.product_type : 'Digital Download'}
                  </>
                )}
              </button>
            </div>
            {cloneListingMutation.isError && (
              <p className="text-sm text-red-600 text-center">
                {cloneListingMutation.error instanceof Error
                  ? cloneListingMutation.error.message
                  : 'Failed to create listing'}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Images */}
        <div className="space-y-6">
          {/* Primary Image */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Primary Image</h2>
              <button
                onClick={() => setShowImageHistory(!showImageHistory)}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <History className="w-4 h-4" />
                History
                {showImageHistory ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
            </div>
            <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
              {primaryAsset ? (
                <img
                  src={primaryAsset.r2_url}
                  alt="Primary"
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="text-center text-gray-400">
                  <Image className="w-12 h-12 mx-auto mb-2" />
                  <p>No image generated</p>
                </div>
              )}
            </div>
          </div>

          {/* Image History */}
          {showImageHistory && (
            <div className="card">
              <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <History className="w-4 h-4" />
                Image Versions
              </h3>
              {!imageHistoryData?.images || imageHistoryData.images.length === 0 ? (
                <p className="text-sm text-gray-500">No previous versions available.</p>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {imageHistoryData.images.map((img: ImageHistoryItem) => (
                    <div
                      key={img.id}
                      className={`relative aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 ${
                        img.is_current ? 'border-green-500' : 'border-transparent'
                      }`}
                    >
                      <img
                        src={img.r2_url}
                        alt={`Version from ${new Date(img.created_at).toLocaleDateString()}`}
                        className="w-full h-full object-cover"
                      />
                      {img.is_current ? (
                        <div className="absolute top-1 right-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded">
                          Current
                        </div>
                      ) : (
                        <button
                          onClick={() => setCurrentImageMutation.mutate(img.id)}
                          disabled={setCurrentImageMutation.isPending}
                          className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center"
                        >
                          <span className="text-white text-xs font-medium flex items-center gap-1">
                            <RotateCcw className="w-3 h-3" />
                            Set as Current
                          </span>
                        </button>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 text-center">
                        {new Date(img.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {setCurrentImageMutation.isError && (
                <p className="text-sm text-red-600 mt-2">
                  {setCurrentImageMutation.error instanceof Error
                    ? setCurrentImageMutation.error.message
                    : 'Failed to set current image'}
                </p>
              )}
            </div>
          )}

          {/* Mockups */}
          {mockups.length > 0 && (
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Mockups</h2>
              <div className="grid grid-cols-2 gap-4">
                {mockups.map((mockup) => (
                  <div
                    key={mockup.id}
                    className="aspect-square bg-gray-100 rounded-lg overflow-hidden"
                  >
                    <img src={mockup.r2_url} alt="Mockup" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Details */}
        <div className="space-y-6">
          {/* Title */}
          <div className="card">
            <label className="label">Title</label>
            <input type="text" value={listing.title || ''} readOnly className="input" />
            <p className="text-xs text-gray-400 mt-1">
              {listing.title?.length || 0}/140 characters
            </p>
          </div>

          {/* Tags */}
          <div className="card">
            <div className="flex items-center justify-between mb-2">
              <label className="label mb-0">Tags (13 max)</label>
              <button
                onClick={() => setShowContentHistory(!showContentHistory)}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <History className="w-4 h-4" />
                Content History
                {showContentHistory ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(() => {
                const tags = parseTags(listing.tags);

                const handleCopyTag = (tag: string) => {
                  navigator.clipboard.writeText(tag);
                  toast.success(`Copied: ${tag}`);
                };

                return tags.length > 0 ? (
                  tags.map((tag: string, i: number) => (
                    <span
                      key={i}
                      onClick={() => handleCopyTag(tag)}
                      className="px-3 py-1 bg-gray-100 rounded-full text-sm cursor-pointer hover:bg-gray-200 transition-colors"
                      title="Click to copy"
                    >
                      {tag}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-400 text-sm">No tags generated</span>
                );
              })()}
            </div>
          </div>

          {/* Content History */}
          {showContentHistory && (
            <div className="card">
              <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <History className="w-4 h-4" />
                Content Version History
              </h3>
              {!contentHistoryData?.history || contentHistoryData.history.length === 0 ? (
                <p className="text-sm text-gray-500">No previous content versions available.</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {contentHistoryData.history.map((item: ContentHistoryItem) => (
                    <div key={item.id} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-sm font-medium text-gray-900 line-clamp-1">
                            {item.title || 'Untitled'}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {new Date(item.created_at).toLocaleString()}
                            {item.recipe_name && (
                              <span className="ml-2 text-purple-600">
                                Recipe: {item.recipe_name}
                              </span>
                            )}
                            {item.source && (
                              <span className="ml-2 text-gray-400">({item.source})</span>
                            )}
                          </p>
                        </div>
                        <button
                          onClick={() => restoreContentMutation.mutate(item.id)}
                          disabled={restoreContentMutation.isPending}
                          className="btn btn-secondary btn-sm flex items-center gap-1"
                        >
                          {restoreContentMutation.isPending ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <RotateCcw className="w-3 h-3" />
                          )}
                          Restore
                        </button>
                      </div>
                      {item.tags && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(() => {
                            const historyTags = parseTags(item.tags);
                            return (
                              <>
                                {historyTags.slice(0, 5).map((tag, i) => (
                                  <span
                                    key={i}
                                    className="px-1.5 py-0.5 bg-gray-200 rounded text-xs text-gray-600"
                                  >
                                    {tag}
                                  </span>
                                ))}
                                {historyTags.length > 5 && (
                                  <span className="text-xs text-gray-400">
                                    +{historyTags.length - 5} more
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {restoreContentMutation.isError && (
                <p className="text-sm text-red-600 mt-2">
                  {restoreContentMutation.error instanceof Error
                    ? restoreContentMutation.error.message
                    : 'Failed to restore content'}
                </p>
              )}
              {restoreContentMutation.isSuccess && (
                <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Content restored successfully
                </p>
              )}
            </div>
          )}

          {/* Etsy Category */}
          <div className="card">
            <label className="label">Etsy Category</label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="input w-full text-left flex items-center justify-between"
                disabled={categoriesLoading}
              >
                <span className={currentCategoryPath ? 'text-gray-900' : 'text-gray-400'}>
                  {categoriesLoading
                    ? 'Loading categories...'
                    : currentCategoryPath || 'Select a category'}
                </span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`}
                />
              </button>

              {showCategoryDropdown && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      type="text"
                      placeholder="Search categories..."
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                      className="input w-full text-sm"
                      autoFocus
                    />
                  </div>
                  <div className="overflow-y-auto max-h-60">
                    {filteredCategories.length === 0 ? (
                      <div className="p-3 text-sm text-gray-500 text-center">
                        No categories found
                      </div>
                    ) : (
                      filteredCategories.map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => updateCategoryMutation.mutate(cat.id)}
                          disabled={updateCategoryMutation.isPending}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                            listing.etsy_category_id === cat.id
                              ? 'bg-orange-50 text-orange-700'
                              : ''
                          }`}
                          style={{ paddingLeft: `${12 + cat.indent * 16}px` }}
                        >
                          {cat.name}
                          {cat.level === 0 && (
                            <span className="ml-2 text-xs text-gray-400">Top level</span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            {listing.etsy_category_id && (
              <p className="text-xs text-gray-400 mt-1">Category ID: {listing.etsy_category_id}</p>
            )}
            {updateCategoryMutation.isPending && (
              <p className="text-xs text-orange-500 mt-1 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Updating category...
              </p>
            )}
            {updateCategoryMutation.isError && (
              <p className="text-xs text-red-500 mt-1">
                Failed to update category. Please try again.
              </p>
            )}
          </div>

          {/* Description */}
          <div className="card">
            <label className="label">Description</label>
            <div
              className="prose prose-sm max-w-none p-3 bg-gray-50 rounded-lg min-h-[200px]"
              dangerouslySetInnerHTML={{
                __html:
                  listing.description || '<p class="text-gray-400">No description generated</p>',
              }}
            />
          </div>

          {/* Printify POD */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Printer className="w-5 h-5 text-purple-500" />
                <h2 className="text-lg font-semibold">Print on Demand</h2>
              </div>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  listing.product_type === 'printify_pod'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {listing.product_type === 'printify_pod' ? 'POD Product' : 'Digital Download'}
              </span>
            </div>

            {listing.printify_product_id ? (
              // Already has Printify product
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">Printify product created</span>
                </div>
                {(() => {
                  const matchingConfig = printifyConfigs?.configs?.find(
                    (c) => c.blueprint_id === listing.printify_blueprint_id
                  );
                  return (
                    <div className="text-sm text-gray-500 space-y-1">
                      {matchingConfig && (
                        <p className="flex items-center gap-2">
                          <span className="font-medium text-gray-700">{matchingConfig.name}</span>
                          {matchingConfig.product_model && (
                            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                              {matchingConfig.product_model}
                            </span>
                          )}
                        </p>
                      )}
                      <p>Product ID: {listing.printify_product_id}</p>
                      {listing.base_price && <p>Price: ${(listing.base_price / 100).toFixed(2)}</p>}
                    </div>
                  );
                })()}
                <button
                  onClick={() => publishPrintifyMutation.mutate()}
                  disabled={publishPrintifyMutation.isPending}
                  className="btn btn-primary w-full flex items-center justify-center gap-2"
                >
                  {publishPrintifyMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Confirming...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Confirm Draft in Printify
                    </>
                  )}
                </button>
                {publishPrintifyMutation.isSuccess && (
                  <p className="text-sm text-green-600 text-center">Saved as draft in Printify!</p>
                )}
                {publishPrintifyMutation.isError && (
                  <p className="text-sm text-red-600 text-center">
                    {publishPrintifyMutation.error instanceof Error
                      ? publishPrintifyMutation.error.message
                      : 'Failed to publish'}
                  </p>
                )}
              </div>
            ) : primaryAsset ? (
              // Has image, can create POD product
              <div className="space-y-3">
                {!showPrintifyPanel ? (
                  <button
                    onClick={() => setShowPrintifyPanel(true)}
                    className="btn btn-secondary w-full flex items-center justify-center gap-2"
                  >
                    <Package className="w-4 h-4" />
                    Create POD Product
                  </button>
                ) : (
                  <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                    <label className="label">Select Product Type</label>
                    {printifyConfigs?.configs && printifyConfigs.configs.length > 0 ? (
                      <div className="space-y-2">
                        {printifyConfigs.configs.map((config) => (
                          <button
                            key={config.id}
                            onClick={() => setSelectedConfig(config)}
                            className={`w-full text-left p-3 rounded-lg border transition-colors ${
                              selectedConfig?.id === config.id
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{config.name}</span>
                                {config.product_model && (
                                  <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                                    {config.product_model}
                                  </span>
                                )}
                              </div>
                              {config.is_default === 1 && (
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                  Default
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              ${(config.default_price / 100).toFixed(2)} · {config.product_type}
                            </p>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">
                        No product presets configured. Go to Settings to add one.
                      </p>
                    )}
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => {
                          setShowPrintifyPanel(false);
                          setSelectedConfig(null);
                        }}
                        className="btn btn-secondary flex-1"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() =>
                          selectedConfig && createPrintifyProductMutation.mutate(selectedConfig)
                        }
                        disabled={!selectedConfig || createPrintifyProductMutation.isPending}
                        className="btn btn-primary flex-1 flex items-center justify-center gap-2"
                      >
                        {createPrintifyProductMutation.isPending ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          'Create Product'
                        )}
                      </button>
                    </div>
                    {createPrintifyProductMutation.isError && (
                      <p className="text-sm text-red-600 text-center">
                        {createPrintifyProductMutation.error instanceof Error
                          ? createPrintifyProductMutation.error.message
                          : 'Failed to create product'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              // No image yet
              <p className="text-sm text-gray-500">
                Generate an image first to create a POD product.
              </p>
            )}
          </div>

          {/* Model Info */}
          <div className="card bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Generation Info</h3>
            <div className="text-sm text-gray-500 space-y-1">
              <p>Text Model: {listing.text_model_id || 'N/A'}</p>
              <p>Image Model: {listing.image_model_id || 'N/A'}</p>
            </div>
          </div>

          {/* Processing Jobs */}
          {processingJobs.length > 0 && (
            <div className="card">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-5 h-5 text-blue-500" />
                <h2 className="text-lg font-semibold">Processing Jobs</h2>
              </div>
              <div className="space-y-3">
                {processingJobs.map((job) => {
                  const statusInfo = getJobStatusInfo(job.status);
                  const StatusIcon = statusInfo.icon;
                  return (
                    <div key={job.id} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">{getJobTypeLabel(job.job_type)}</span>
                        <span
                          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.color}`}
                        >
                          <StatusIcon
                            className={`w-3 h-3 ${statusInfo.animate ? 'animate-spin' : ''}`}
                          />
                          {statusInfo.label}
                        </span>
                      </div>
                      {job.progress > 0 && job.status !== 'complete' && job.status !== 'failed' && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                            <span>Progress</span>
                            <span>{job.progress}%</span>
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-500 transition-all duration-300"
                              style={{ width: `${job.progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                      {job.last_error && (
                        <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-600">
                          {job.last_error}
                        </div>
                      )}
                      <div className="mt-2 text-xs text-gray-400">
                        Started: {new Date(job.created_at).toLocaleString()}
                        {job.status === 'complete' && (
                          <> | Completed: {new Date(job.updated_at).toLocaleString()}</>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
